import './muiLicense';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './assets/styles/base/yt-index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import 'bootstrap/dist/css/bootstrap.min.css';

import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';

// Define custom colors using CSS variables
const customMainBlue = getComputedStyle(document.documentElement).getPropertyValue('--ywmainblue').trim();

const customOrange = getComputedStyle(document.documentElement).getPropertyValue('--ywnotificationorange').trim();
const customWhite = getComputedStyle(document.documentElement).getPropertyValue('--ywnotificationwhite').trim();
const customSuccess = getComputedStyle(document.documentElement).getPropertyValue('--ywnotificationsuccess').trim();
const customNormal = getComputedStyle(document.documentElement).getPropertyValue('--ywnotificationnormal').trim();
const customWarning = getComputedStyle(document.documentElement).getPropertyValue('--ywnotificationwarning').trim();
const customError = getComputedStyle(document.documentElement).getPropertyValue('--ywnotificationerror').trim();

const customButtonBlue = getComputedStyle(document.documentElement).getPropertyValue('--ywbuttonblue').trim();
const customButtonBlack = getComputedStyle(document.documentElement).getPropertyValue('--ywbuttonblack').trim();

// Create a theme with a custom color
const theme = createTheme({
  palette: {
    ywMainBlue: {
      main: customMainBlue,
    },
    ywNotificationOrange: {
      main: customOrange,
      contrastText: customWhite // Optional: text color
    },
    ywNotificationSuccess: {
      main: customSuccess,
      contrastText: customWhite // Optional: text color
    },
    ywNotificationNormal: {
      main: customNormal,
      contrastText: customWhite // Optional: text color
    },
    ywNotificationWarning: {
      main: customWarning,
      contrastText: customWhite // Optional: text color
    },
    ywNotificationError: {
      main: customError,
      contrastText: customWhite // Optional: text color
    },
    ywButtonBlue: {
      main: customButtonBlue,
      contrastText: customWhite // Optional: text color
    },
    ywButtonBlack: {
      main: customButtonBlack,
      contrastText: customWhite // Optional: text color
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          if (ownerState.color === 'ywNotificationOrange') {
            return {
              color: theme.palette.ywNotificationOrange.contrastText,
              textTransform: 'none',
              border: `2px solid ${theme.palette.ywNotificationOrange.contrastText}`,
              '&:hover': {
                backgroundColor: theme.palette.ywNotificationOrange.contrastText,
                color: theme.palette.ywNotificationOrange.main // Ensure text color remains the same on hover
              }
            };
          }
          if (ownerState.color === 'ywNotificationSuccess') {
            return {
              color: theme.palette.ywNotificationSuccess.contrastText,
              textTransform: 'none',
              border: `2px solid ${theme.palette.ywNotificationSuccess.contrastText}`,
              '&:hover': {
                backgroundColor: theme.palette.ywNotificationSuccess.contrastText,
                color: theme.palette.ywNotificationSuccess.main // Ensure text color remains the same on hover
              }
            };
          }
          if (ownerState.color === 'ywNotificationNormal') {
            return {
              color: theme.palette.ywNotificationNormal.contrastText,
              textTransform: 'none',
              border: `2px solid ${theme.palette.ywNotificationNormal.contrastText}`,
              '&:hover': {
                backgroundColor: theme.palette.ywNotificationNormal.contrastText,
                color: theme.palette.ywNotificationNormal.main // Ensure text color remains the same on hover
              }
            };
          }
          if (ownerState.color === 'ywNotificationWarning') {
            return {
              color: theme.palette.ywNotificationWarning.contrastText,
              textTransform: 'none',
              border: `2px solid ${theme.palette.ywNotificationWarning.contrastText}`,
              '&:hover': {
                backgroundColor: theme.palette.ywNotificationWarning.contrastText,
                color: theme.palette.ywNotificationWarning.main // Ensure text color remains the same on hover
              }
            };
          }
          if (ownerState.color === 'ywNotificationError') {
            return {
              color: theme.palette.ywNotificationError.contrastText,
              textTransform: 'none',
              border: `2px solid ${theme.palette.ywNotificationError.contrastText}`,
              '&:hover': {
                backgroundColor: theme.palette.ywNotificationError.contrastText,
                color: theme.palette.ywNotificationError.main // Ensure text color remains the same on hover
              }
            };
          }
          if (ownerState.color === 'ywButtonBlack') {
            return {
              backgroundColor: theme.palette.ywButtonBlack.main,
              color: theme.palette.ywButtonBlack.contrastText,
            };
          }
          if (ownerState.color === 'ywButtonBlue') {
            return {
              backgroundColor: theme.palette.ywButtonBlue.main,
              color: theme.palette.ywButtonBlue.contrastText,
            };
          }
          if (ownerState.color === 'ywButtonWhite') {
            return {
              backgroundColor: theme.palette.ywButtonBlue.contrastText,
              color: theme.palette.ywButtonBlue.main,
              borderColor: theme.palette.ywButtonBlue.main,
            };
          }
          if (ownerState.color === 'ywTextBlue') {
            return {
              color: theme.palette.ywButtonBlue.main,
            };
          }
          return {};
        },
      },
    },

    MuiAutocomplete: {
      variants: [
        {
          props: { variant: 'blueBorderVariant' },
          style: {
            // Border color for the input
            '& .MuiOutlinedInput-root': {
              padding: '2px',
              paddingLeft: '6px',
              '& fieldset': {
                borderColor: '#5A73EA', // default border color
              },
              '&:hover fieldset': {
                borderColor: '#5A73EA', // hover border color
              },
              '&.Mui-focused fieldset': {
                borderColor: '#5A73EA', // focused border color
              },
            },
            // Label color
            '& label': {
              color: '#5A73EA',
            },
            '& label.Mui-focused': {
              color: '#5A73EA',
            },
            // Clear icon color
            '& .MuiAutocomplete-clearIndicator': {
              color: '#5A73EA',
            },
            // Dropdown icon color
            '& .MuiAutocomplete-popupIndicator': {
              color: '#5A73EA',
            },
          },
        },
        {
          props: { variant: 'defaultVariant' },
          style: {
            // Border color for the input
            '& .MuiOutlinedInput-root': {
              padding: '2px',
              paddingLeft: '6px',
              '&:hover fieldset': {
                borderColor: '#5A73EA', // hover border color
              },
              '&.Mui-focused fieldset': {
                borderColor: '#5A73EA', // focused border color
              },
              // Disabled state - remove hover effects
              '&.Mui-disabled': {
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)', // Default disabled border color, no blue hover
                },
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)', // Default disabled border color
                },
              },
            },
            '& label.Mui-focused': {
              color: '#5A73EA',
            },
            '&:hover fieldset': {
              borderColor: '#5A73EA', // hover border color
            },
            '&.Mui-focused fieldset': {
              borderColor: '#5A73EA', // focused border color
            },
            // Clear icon color
            '& .MuiAutocomplete-clearIndicator': {
              color: '#5A73EA',
            },
            // Dropdown icon color
            '& .MuiAutocomplete-popupIndicator': {
              color: '#5A73EA',
            },
          
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'nowrap',
            },
            '& .MuiAutocomplete-tag': {
              maxWidth: 'calc(100% - 40px)',
            },
            '& .MuiAutocomplete-input': {
              minWidth: '0 !important',
              flex: '0 0 auto',
            },
            // Custom summary chips (e.g. "All (24)" / "Multiple (334)") don't get the
            // MuiAutocomplete-tag class since they're rendered manually via renderTags,
            // so truncate them directly to keep the field a single, fixed-height row.
            '& .MuiChip-root': {
              maxWidth: 'calc(100% - 8px)',
            },
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          },
        },
      ],
    },

  },
});


const root = ReactDOM.createRoot(document.getElementById('root'));

// Suppress benign ResizeObserver loop warnings that CRA dev overlay reports as runtime errors.
// See: https://github.com/WICG/resize-observer/issues/38
const resizeObserverMessages = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded'
];

const isBenignResizeObserverMessage = (value) => {
  if (!value) return false;
  const text = typeof value === 'string' ? value : (value.message || '');
  return resizeObserverMessages.some((message) => text.includes(message));
};

// Make ResizeObserver callbacks async to avoid "loop completed" warnings
// caused by sync layout thrashing in complex viewers during pane resize/collapse.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserverPatched extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          }
          catch (error) {
            if (!isBenignResizeObserverMessage(error)) {
              throw error;
            }
          }
        });
      });
    }
  };
}

window.onerror = (message) => {
  if (isBenignResizeObserverMessage(message)) {
    return true;
  }
  return false;
};

window.onunhandledrejection = (event) => {
  if (isBenignResizeObserverMessage(event?.reason)) {
    event.preventDefault();
    return true;
  }
  return false;
};

window.addEventListener('error', (e) => {
  if (isBenignResizeObserverMessage(e?.message) || isBenignResizeObserverMessage(e?.error)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

window.addEventListener('unhandledrejection', (e) => {
  if (isBenignResizeObserverMessage(e?.reason)) {
    e.preventDefault();
  }
});

const nativeConsoleError = console.error;
console.error = (...args) => {
  if (args.some((arg) => isBenignResizeObserverMessage(arg))) {
    return;
  }
  nativeConsoleError(...args);
};

root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <App />
  </ThemeProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
